import { action } from '@ember/object';
import { restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
// @ts-ignore
import BufferIconCell from 'volta/components/cell/buffer-icon-cell';
// @ts-ignore
import CodeDescCell from 'volta/components/cell/code-desc-cell';
// @ts-ignore
import LabelCell from 'volta/components/cell/label-cell';
// @ts-ignore
import PlanningPriorityCell from 'volta/components/cell/planning-priority-cell';
import TaskCell from 'volta/components/cell/task-cell';
import InputFilter from 'volta/components/filters/input-filter';
import SelectFilter from 'volta/components/filters/select-filter';
import { BufferZonesOptions } from 'volta/models/constants/buffer-zones';
import { PlanningMethodologiesOptions } from 'volta/models/constants/planning-methodologies';
import { ResourceName } from 'volta/models/order-sync-alert';
import { SkuResourceName } from 'volta/models/sku';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { inFilter } from 'volta/utils/filters-utils';
import { getBufferZoneCriticality } from 'volta/utils/style-utils';

import ArrayProxy from '@ember/array/proxy';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type IntlService from 'ember-intl/services/intl';
import type SkuModalService from 'volta/services/sku-modal-service';
import type LabelService from 'volta/services/label-service';
import type PlanningOrder from 'volta/models/planning-order';
import type OrderSyncAlert from 'volta/models/order-sync-alert';
import type RouterService from '@ember/routing/router-service';
import type StoreService from 'volta/services/store';
import type BvFlashService from 'volta/services/bv-flash';
import type { IJsonApiQuery, SingleResourceDoc } from 'volta/utils/api/jsonapi-types';
import type EventStreamService from 'volta/services/event-stream';
import { TaskListChangeEvents } from 'volta/models/task';
import { TDateField } from 'volta/models/types/planning-order';
import { POResourceName } from 'volta/models/planning-order';
import { ISupplierIdentityInfo } from 'volta/models/types/supplier';
interface IArgs {
  planningOrders: PlanningOrder[];
  isExternalUser?: boolean;
  dateField: TDateField;
  disabledPO?: boolean;
  skuRoute?: string;
  onClose?: () => void;
}

const SYNC_ALERTS_PAGE_LIMIT = 10000;
export default class PlanningOrderListSyncAlerts extends Component<IArgs> {
  @service intl!: IntlService;
  @service skuModalService!: SkuModalService;
  @service labelService!: LabelService;
  @service router!: RouterService;
  @service store!: StoreService;
  @service bvFlash!: BvFlashService;
  @service eventStream!: EventStreamService;

  @tracked selectedAlerts: OrderSyncAlert[] = [];
  @tracked queryParams = { bufferZone: '0|1' };
  @tracked alerts: OrderSyncAlert[] = [];
  @tracked aggregatedPO?: PlanningOrder;

  resourceName = ResourceName;

  constructor(owner: any, args: IArgs) {
    super(owner, args);
    this.eventStream.addHandler(
      SkuResourceName.plural,
      TaskListChangeEvents,
      this.handleTaskEvent,
      this
    );
  }

  @action
  handleDidInsert() {
    let firstPO: Record<string, any> = {};
    const suppliers: Record<string, ISupplierIdentityInfo> = {};
    const { planningOrders } = this.args;

    if (planningOrders.length === 0) {
      return;
    }

    if (planningOrders.length === 1) {
      this.aggregatedPO = planningOrders[0];
    } else {
      planningOrders.forEach((po, i) => {
        if (i === 0) {
          firstPO = po.toJSON();
        } else {
          firstPO.reorderQty += po.reorderQty;
          if (po.supplierId && !suppliers[po.supplierId]) {
            suppliers[po.supplierId] = po.supplierInfo!;
          }
        }
      });
      if (Object.keys(firstPO).length > 0) {
        this.aggregatedPO = this.store.createRecord(POResourceName.singular, {
          ...firstPO,
          supplierInfo:
            Object.keys(suppliers).length > 1
              ? { supplierId: 'NA', code: `${suppliers.length} ${this.intl.t('suppliers')}` }
              : undefined
        });
      }
    }
  }

  get hasMultiplePO() {
    return this.args.planningOrders.length > 1;
  }

  get disabledPO() {
    return this.args.disabledPO || this.args.planningOrders.length !== 1;
  }

  get filterDefinitions() {
    const defs: Array<TFilterDefinition> = [
      {
        queryParam: 'q',
        apiParam: 'q',
        comparators: ['lk'],
        hidden: true,
        component: InputFilter,
        componentArgs: { inputType: 'text' }
      },
      {
        queryParam: 'bufferZone',
        apiParam: 'bufferZone',
        comparators: ['in'],
        isPrimary: true,
        component: SelectFilter,
        componentArgs: { possibleValues: BufferZonesOptions }
      },
      {
        queryParam: 'methodology',
        apiParam: 'methodology',
        comparators: ['in'],
        isPrimary: true,
        component: SelectFilter,
        componentArgs: { possibleValues: PlanningMethodologiesOptions }
      }
    ];
    return defs;
  }

  @cached
  get skuIds() {
    return this.alerts.map((a) => a.sku.skuId).uniq();
  }

  get columns() {
    const { intl } = this;
    const { isExternalUser = false } = this.args;
    return [
      {
        columnName: intl.t('part'),
        valuePath: 'sku.partCode',
        sortKey: 'parent',
        isResizable: true,
        width: 160,
        cellComponent: CodeDescCell,
        format: (model: OrderSyncAlert) => {
          return {
            code: model.sku.partCode,
            description: model.sku.skuDescription,
            link: isExternalUser ? undefined : this.args.skuRoute,
            linkParam: isExternalUser ? undefined : model.sku.skuId,
            action: this.args.skuRoute
              ? undefined
              : () => this.skuModalService.showWithSku('planning', model.sku),
            context: this
          };
        }
      },
      isExternalUser
        ? undefined
        : {
            columnName: '',
            valuePath: 'sku.methodology',
            sortKey: 'sku.methodology',
            isResizable: false,
            isReorderable: true,
            width: 35,
            cellComponent: BufferIconCell,
            format: (model: OrderSyncAlert) => {
              return {
                methodology: model.sku.methodology,
                isProtected: model.sku.isProtected
              };
            }
          },
      {
        columnName: intl.t('acqCodeShort'),
        valuePath: 'sku.acqCode',
        width: 90,
        isReorderable: true,
        isResizable: false,
        cellComponent: LabelCell,
        format: (model: OrderSyncAlert) => {
          const { acqCode } = model.sku;
          return {
            label: intl.t(`acqCodes.${acqCode || 'UNKNOWN'}`)
          };
        }
      },
      {
        columnName: intl.t('tasksPage.title'),
        valuePath: 'tasks',
        width: 75,
        maxWidth: 100,
        isReorderable: true,
        isResizable: true,
        isSortable: false,
        cellComponent: TaskCell,
        format: (model: OrderSyncAlert) => {
          return {
            entityId: model.sku?.skuId,
            taskType: 'EXECUTION_TASK',
            resourceName: SkuResourceName,
            aggStats: model.tasks
          };
        }
      },
      {
        columnName: intl.t('qty'),
        sortKey: 'qty',
        valuePath: 'qty',
        isReorderable: true,
        isResizable: true,
        width: 90,
        textAlign: 'right',
        classNames: 'u-text-mono',
        format: (model: OrderSyncAlert) => {
          return `${intl.formatNumber(model.qty)} ${this.labelService.unitMeasure(
            model.sku.partUnitMeasure
          )}`;
        }
      },
      {
        columnName: intl.t('dueDate'),
        sortKey: 'dueDate',
        valuePath: 'dueDate',
        isReorderable: true,
        isResizable: false,
        width: 90,
        textAlign: 'right',
        format: (model: OrderSyncAlert) => {
          return moment(model.dueDate).format('L');
        }
      },
      {
        columnName: intl.t('executionAlertsPage.title'),
        sortKey: 'priority',
        valuePath: 'bufferPenetration',
        width: 90,
        isReorderable: true,
        isResizable: true,
        cellComponent: PlanningPriorityCell,
        textAlign: 'left',
        format: (model: OrderSyncAlert) => {
          return {
            priorityName: getBufferZoneCriticality(model.bufferZone),
            bufferPenetration: model.bufferPenetration,
            action: () =>
              this.args.skuRoute
                ? this.router.transitionTo(this.args.skuRoute, model.sku.skuId, {
                    queryParams: { tab: 'execution' }
                  })
                : this.skuModalService.showWithSku('execution', model.sku),
            context: this,
            displayEmptyText: true,
            emptyTextLabel:
              !model.sku.methodology || model.sku.methodology === 'NONE'
                ? 'planningMethodologies.NONE'
                : 'syncAlerts.NONE'
          };
        }
      }
    ].filter(Boolean);
  }

  async handleTaskEvent(payload: SingleResourceDoc) {
    const { linkedEntityId, taskType } = payload.data.attributes ?? {};

    if (
      taskType === 'EXECUTION_TASK' &&
      linkedEntityId &&
      this.skuIds.includes(linkedEntityId as string)
    ) {
      await this.store.query(ResourceName.singular, {
        filter: { skuId: inFilter([linkedEntityId as string]) }
      });
    }
  }

  queryAlertsTask = restartableTask(async (apiQuery: IJsonApiQuery = {}) => {
    try {
      const filter = { ...(apiQuery.filter ?? {}) };

      if (this.args.planningOrders.length === 0) {
        return [];
      } else if (this.args.planningOrders.length > 20) {
        const skuId = this.args.planningOrders[0].skuId;
        filter.skuId = inFilter([skuId]);
      } else {
        const poIds = this.args.planningOrders.map((po) => po.id);
        filter.planningOrderId = inFilter(poIds);
      }

      const query = {
        ...apiQuery,
        filter,
        page: {
          offset: 0,
          limit: SYNC_ALERTS_PAGE_LIMIT
        }
      };

      const response: ArrayProxy<OrderSyncAlert> = await this.store.query(
        ResourceName.singular,
        query
      );
      this.alerts = response.toArray();
      return this.alerts.filter((o) => !o.parentNode);
    } catch (error) {
      this.bvFlash;
      return defaultErrorHandler(error);
    }
  });
}
